import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Form, Input, Select, Space, Table, Tag, Typography, message } from "antd";
import {
  createAgentSeoAiDraft,
  createAgentSeoDraft,
  deleteAgentSeoArticle,
  fetchAgentSeoArticles,
  patchAgentSeoArticle,
  publishAgentSeoArticle,
} from "../../../services/agentSeoArticlesApi";
import { fetchAgentSeoDashboardSummary } from "../../../services/agentSeoDashboardApi";

const { Text, Title } = Typography;
const { TextArea } = Input;

const CATEGORY_OPTIONS = [
  { value: "travel-guide", label: "מדריך יעד" },
  { value: "attractions", label: "אטרקציות" },
  { value: "hotels", label: "מלונות" },
  { value: "vacation-packages", label: "חבילות נופש" },
];

const TONE_OPTIONS = [
  { value: "professional", label: "מקצועי" },
  { value: "friendly", label: "ידידותי" },
  { value: "premium", label: "פרימיום" },
  { value: "family", label: "משפחתי" },
];

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  try {
    return new Date(value).toLocaleString("he-IL");
  } catch {
    return String(value);
  }
};

export default function AgentSeoArticlesPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [articles, setArticles] = useState([]);
  const [error, setError] = useState(null);
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const [aiQuota, setAiQuota] = useState({
    seo_ai_daily_quota: 5,
    seo_ai_used_today: 0,
    seo_ai_remaining_today: 5,
  });

  const [draftForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAgentSeoArticles();
      setArticles(Array.isArray(rows) ? rows : []);
      if (selectedArticleId) {
        const selected = (rows || []).find((row) => Number(row.id) === Number(selectedArticleId));
        if (selected) {
          editForm.setFieldsValue({
            title: selected.title || "",
            slug: selected.slug || "",
            meta_title: selected.meta_title || "",
            meta_description: selected.meta_description || "",
            content_markdown: selected.content_markdown || "",
          });
        } else {
          setSelectedArticleId(null);
          editForm.resetFields();
        }
      }
    } catch (e) {
      setError(e?.response?.data?.message || "טעינת מאמרי SEO נכשלה");
    } finally {
      setLoading(false);
    }
  };

  const loadAiQuota = async () => {
    try {
      const data = await fetchAgentSeoDashboardSummary();
      if (!data || typeof data !== "object") {
        return;
      }
      setAiQuota({
        seo_ai_daily_quota: Number(data.seo_ai_daily_quota || 5),
        seo_ai_used_today: Number(data.seo_ai_used_today || 0),
        seo_ai_remaining_today: Number(data.seo_ai_remaining_today || 0),
      });
    } catch {
      setAiQuota({
        seo_ai_daily_quota: 5,
        seo_ai_used_today: 0,
        seo_ai_remaining_today: 5,
      });
    }
  };

  useEffect(() => {
    loadArticles().catch(() => {});
    loadAiQuota().catch(() => {});
  }, []);

  const selectedArticle = useMemo(
    () => articles.find((row) => Number(row.id) === Number(selectedArticleId)) || null,
    [articles, selectedArticleId]
  );

  const articleColumns = [
    {
      title: "כותרת",
      dataIndex: "title",
      key: "title",
      render: (value, row) => (
        <Button type="link" onClick={() => setSelectedArticleId(row.id)} style={{ paddingInline: 0 }}>
          {value}
        </Button>
      ),
    },
    { title: "slug", dataIndex: "slug", key: "slug", render: (value) => <Text code>{value}</Text> },
    { title: "יעד", dataIndex: "destination", key: "destination" },
    { title: "קטגוריה", dataIndex: "category", key: "category" },
    {
      title: "סטטוס",
      dataIndex: "status",
      key: "status",
      inlineSize: 120,
      render: (value) => (
        <Tag color={value === "published" ? "green" : value === "archived" ? "default" : "orange"}>
          {value}
        </Tag>
      ),
    },
    { title: "עודכן", dataIndex: "updated_at", key: "updated_at", render: (value) => formatDate(value) },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      render: (value) => (value ? <Text code>{value}</Text> : <Text type="secondary">-</Text>),
    },
    {
      title: "פעולות",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Button
            onClick={async () => {
              setError(null);
              setPublishingId(row.id);
              try {
                const published = await publishAgentSeoArticle(row.id);
                message.success(`המאמר פורסם: ${published?.url || `/blog/${row.slug}`}`);
                await loadArticles();
              } catch (e) {
                const msg = e?.response?.data?.message || "פרסום מאמר נכשל";
                setError(msg);
                message.error(msg);
              } finally {
                setPublishingId(null);
              }
            }}
            disabled={row.status !== "draft"}
            loading={publishingId === row.id}
          >
            פרסום
          </Button>
          <Button
            danger={row.status === "draft"}
            onClick={async () => {
              const isPublished = row.status === "published";
              const confirmMessage = isPublished
                ? "מאמר שפורסם יועבר לארכיון ולא יימחק. להמשיך?"
                : "האם למחוק את הטיוטה?";
              if (!window.confirm(confirmMessage)) {
                return;
              }
              setError(null);
              try {
                const result = await deleteAgentSeoArticle(row.id);
                if (result?.action === "archived") {
                  message.success("המאמר הועבר לארכיון");
                } else if (result?.action === "deleted") {
                  message.success("הטיוטה נמחקה");
                } else {
                  message.success("הפעולה הושלמה");
                }
                await loadArticles();
              } catch (e) {
                const msg = e?.response?.data?.message || "מחיקת מאמר נכשלה";
                setError(msg);
                message.error(msg);
              }
            }}
            data-testid={`delete-or-archive-${row.id}`}
            disabled={row.status === "archived"}
          >
            {row.status === "published" ? "העבר לארכיון" : row.status === "draft" ? "מחק" : "בארכיון"}
          </Button>
        </Space>
      ),
    },
  ];

  const handleCreateDraft = async (values) => {
    setSaving(true);
    setError(null);
    try {
      const article = await createAgentSeoDraft(values);
      if (article?.id) {
        message.success("טיוטה חדשה נוצרה");
      }
      draftForm.resetFields();
      await loadArticles();
      await loadAiQuota();
      if (article?.id) {
        setSelectedArticleId(article.id);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "יצירת טיוטה נכשלה";
      setError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAiDraft = async () => {
    if (aiQuota.seo_ai_remaining_today <= 0) {
      setError("מכסת יצירות AI להיום נוצלה");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const values = await draftForm.validateFields();
      const result = await createAgentSeoAiDraft(values);
      if (result?.article?.id) {
        message.success(result?.message || "נוצרה טיוטה עם AI");
      }
      draftForm.resetFields();
      await loadArticles();
      await loadAiQuota();
      if (result?.article?.id) {
        setSelectedArticleId(result.article.id);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "יצירת טיוטת AI נכשלה";
      setError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveArticle = async (values) => {
    if (!selectedArticle) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await patchAgentSeoArticle(selectedArticle.id, values);
      message.success("הטיוטה עודכנה");
      await loadArticles();
    } catch (e) {
      const msg = e?.response?.data?.message || "עדכון טיוטה נכשל";
      setError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ inlineSize: "100%" }}>
      <Card title="SEO Content Engine — Draft Articles">
        <Text type="secondary">
          MVP: יצירת טיוטות בלבד. אין פרסום אוטומטי ואין AI חיצוני בשלב זה.
        </Text>
      </Card>

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Card title="צור טיוטה חדשה">
        <Form
          form={draftForm}
          layout="vertical"
          onFinish={handleCreateDraft}
          initialValues={{ category: "travel-guide", tone: "professional" }}
        >
          <Form.Item label="יעד" name="destination" rules={[{ required: true, message: "יש למלא יעד" }]}>
            <Input placeholder="Rhodes" />
          </Form.Item>
          <Form.Item label="קטגוריה" name="category" rules={[{ required: true, message: "יש לבחור קטגוריה" }]}>
            <Select options={CATEGORY_OPTIONS} />
          </Form.Item>
          <Form.Item label="נושא" name="topic" rules={[{ required: true, message: "יש למלא נושא" }]}>
            <Input placeholder="Best beaches in Rhodes" />
          </Form.Item>
          <Form.Item label="טון כתיבה" name="tone" rules={[{ required: true, message: "יש לבחור טון" }]}>
            <Select options={TONE_OPTIONS} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              צור טיוטה חדשה
            </Button>
            <Button
              onClick={handleCreateAiDraft}
              loading={saving}
              data-testid="create-ai-draft-button"
              disabled={aiQuota.seo_ai_remaining_today <= 0}
            >
              צור טיוטה עם AI
            </Button>
          </Space>
          <Text data-testid="ai-quota-counter">
            נותרו היום {aiQuota.seo_ai_remaining_today} מתוך {aiQuota.seo_ai_daily_quota}
          </Text>
          {aiQuota.seo_ai_remaining_today <= 0 ? (
            <Alert
              type="warning"
              showIcon
              message="מכסת יצירות AI להיום נוצלה"
              data-testid="ai-quota-exhausted-message"
            />
          ) : null}
        </Form>
      </Card>

      <Card title="רשימת מאמרים" loading={loading}>
        <Table rowKey="id" dataSource={articles} columns={articleColumns} pagination={{ pageSize: 8 }} />
      </Card>

      <Card title="עריכת טיוטה">
        {!selectedArticle ? (
          <Text type="secondary">בחרו מאמר מהרשימה כדי לערוך ולפרסם.</Text>
        ) : (
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleSaveArticle}
            initialValues={{
              title: selectedArticle.title || "",
              slug: selectedArticle.slug || "",
              meta_title: selectedArticle.meta_title || "",
              meta_description: selectedArticle.meta_description || "",
              content_markdown: selectedArticle.content_markdown || "",
            }}
            key={selectedArticle.id}
          >
            <Form.Item label="Title" name="title" rules={[{ required: true, message: "title הוא שדה חובה" }]}>
              <Input maxLength={70} />
            </Form.Item>
            <Form.Item label="Slug" name="slug" rules={[{ required: true, message: "slug הוא שדה חובה" }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Meta Title" name="meta_title">
              <Input maxLength={70} />
            </Form.Item>
            <Form.Item label="Meta Description" name="meta_description">
              <TextArea rows={3} maxLength={160} />
            </Form.Item>
            <Form.Item label="Content (Markdown)" name="content_markdown">
              <TextArea rows={12} />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                שמור טיוטה
              </Button>
              <Button
                onClick={async () => {
                  setError(null);
                  setPublishingId(selectedArticle.id);
                  try {
                    const published = await publishAgentSeoArticle(selectedArticle.id);
                    message.success(`פורסם בהצלחה: ${published?.url || `/blog/${selectedArticle.slug}`}`);
                    await loadArticles();
                  } catch (e) {
                    const msg = e?.response?.data?.message || "פרסום מאמר נכשל";
                    setError(msg);
                    message.error(msg);
                  } finally {
                    setPublishingId(null);
                  }
                }}
                loading={publishingId === selectedArticle.id}
                disabled={selectedArticle.status !== "draft"}
              >
                פרסום
              </Button>
            </Space>
          </Form>
        )}
      </Card>
    </Space>
  );
}
